import { useLiveData } from '@toeverything/infra';
import { useLayoutEffect } from 'react';

import type { View } from '../entities/view';
import { endCoverTiming } from './sidebar-cover';
import { viewContentBox } from './view-content-box';

/**
 * Keeps a layer that spans a view within the view's content: beside the workspace sidebar, beside
 * a navigation or list pane before the main pane, and clear of a sidebar that lies over the view's
 * end. The layer keeps the view's height. It follows the content's and the main pane's edge as they
 * move, and the sidebar over the end as it slides or is dragged.
 */
export function useViewContentInline(layer: HTMLElement | null, view: View) {
  const content = useLiveData(view.contentElement$);
  const mainPane = useLiveData(view.mainPane$);
  const endCover = useLiveData(view.endCover$);
  useLayoutEffect(() => {
    if (!layer) return;
    layer.style.transition = `right ${endCoverTiming(endCover)}`;
    layer.style.right = `${endCover.width}px`;
  }, [endCover, layer]);
  useLayoutEffect(() => {
    const frame = layer?.offsetParent;
    if (!layer || !frame) return;
    if (!content) {
      layer.style.left = '0px';
      return;
    }
    const place = () => {
      const left =
        viewContentBox(content, mainPane).left -
        frame.getBoundingClientRect().left;
      layer.style.left = `${left}px`;
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(content);
    if (mainPane) observer.observe(mainPane);
    return () => observer.disconnect();
  }, [content, layer, mainPane]);
}
